import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createMemberSkill, updateMemberSkill, PROFICIENCY_LEVELS } from "app/services/skill.service";

const initialData = {
  id: null,
  memberId: "",
  skillId: "",
  proficiencyLevel: "INTERMEDIATE",
  yearsOfExperience: 0,
  isPrimary: false,
  notes: "",
};

const AddMemberSkillDialog = ({ open, setOpen, onSuccess, editObject, skills, memberId }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        id: editObject.id,
        memberId: editObject.memberId || memberId,
        skillId: editObject.skill?.id || editObject.skillId || "",
        proficiencyLevel: editObject.proficiencyLevel || "INTERMEDIATE",
        yearsOfExperience: editObject.yearsOfExperience || 0,
        isPrimary: editObject.isPrimary || false,
        notes: editObject.notes || "",
      });
    } else {
      setData({ ...initialData, memberId: memberId || "" });
    }
    setMessageOpen(false);
  }, [editObject, open, memberId]);

  const validateForm = () => {
    if (!data.skillId) {
      setError("Please select a skill");
      return false;
    }
    if (!data.proficiencyLevel) {
      setError("Please select proficiency level");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = { ...data, memberId: memberId || data.memberId };
      if (editObject) {
        await updateMemberSkill(data.id, payload);
        setSuccess("Member skill updated successfully");
      } else {
        await createMemberSkill(payload);
        setSuccess("Member skill added successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Member Skill" : "Add Member Skill"}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="skillId"
            label="Skill"
            fullWidth
            select
            variant="outlined"
            value={data.skillId}
            onChange={(e) => setData({ ...data, skillId: e.target.value })}
            required
            disabled={!!editObject}
          >
            {(skills || []).map((skill) => (
              <MenuItem key={skill.id} value={skill.id}>
                {skill.skillName} ({skill.category?.replace("_", " ")})
              </MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="proficiencyLevel"
            label="Proficiency Level"
            fullWidth
            select
            variant="outlined"
            value={data.proficiencyLevel}
            onChange={(e) => setData({ ...data, proficiencyLevel: e.target.value })}
            required
          >
            {Object.entries(PROFICIENCY_LEVELS).map(([key, value]) => (
              <MenuItem key={key} value={value}>{key}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="yearsOfExperience"
            label="Years of Experience"
            type="number"
            fullWidth
            variant="outlined"
            value={data.yearsOfExperience}
            onChange={(e) => setData({ ...data, yearsOfExperience: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: 0.5 }}
          />
        </MDBox>
        <MDBox my={2}>
          <FormControlLabel
            control={
              <Switch
                checked={data.isPrimary}
                onChange={(e) => setData({ ...data, isPrimary: e.target.checked })}
              />
            }
            label="Primary Skill"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="notes"
            label="Notes"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddMemberSkillDialog);

